import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import type { QueryDocumentSnapshot } from "firebase/firestore";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";

import styles from "./ParticipantsTestResults.module.scss";

import { REPORT_ADMIN_EMAILS } from "@/constants/admin";
import { testCodeToTopicTitle } from "@/constants/common";
import { getParticipantTestResultsPage } from "@/firebase";
import { pages } from "@/routes/constants";

const PAGE_SIZE = 20;

interface ParticipantRow {
  id: string;
  user_id: string;
  test_code: string;
  score_percent: number;
  email: string;
  name: string;
  completed_at: string;
}

interface CachedPage {
  rows: ParticipantRow[];
  pageProfiles: PageProfile[];
  lastVisible: QueryDocumentSnapshot | null;
  hasMore: boolean;
  includedUserIds: string[];
}

interface SearchFieldMode {
  resolvedSearchField: "name_lower" | "name" | null;
  legacyNamePrefix: string | null;
}

interface UserGroup {
  user_id: string;
  name: string;
  email: string;
  tests: ParticipantRow[];
}

interface PageProfile {
  user_id: string;
  name: string;
  email: string;
}

/** Order follows the report page (Firestore `users` query); merges test rows from user_test_results. */
function groupsFromPage(
  rows: ParticipantRow[],
  pageProfiles: PageProfile[]
): UserGroup[] {
  const byUser = new Map<string, ParticipantRow[]>();
  for (const row of rows) {
    const uid = row.user_id?.trim() || `unknown:${row.id}`;
    let list = byUser.get(uid);
    if (!list) {
      list = [];
      byUser.set(uid, list);
    }
    list.push(row);
  }
  byUser.forEach(list => {
    list.sort(
      (a, b) =>
        new Date(b.completed_at).getTime() - new Date(a.completed_at).getTime()
    );
  });
  return pageProfiles.map(p => {
    const uid = p.user_id?.trim() || p.user_id;
    const tests = byUser.get(uid) ?? [];
    let name = p.name || "";
    let email = p.email || "";
    for (const t of tests) {
      if (t.name?.trim()) name = t.name;
      if (t.email?.trim()) email = t.email;
    }
    return {
      user_id: p.user_id,
      name,
      email,
      tests,
    };
  });
}

function displayLabel(g: UserGroup): string {
  return g.name.trim() || g.email.trim() || g.user_id;
}

function getOrCreatePageMap(
  root: Map<string, Map<number, CachedPage>>,
  searchKey: string
): Map<number, CachedPage> {
  let pageMap = root.get(searchKey);
  if (!pageMap) {
    pageMap = new Map();
    root.set(searchKey, pageMap);
  }
  return pageMap;
}

const ParticipantsTestResults: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [appliedSearchRaw, setAppliedSearchRaw] = useState("");
  const [rows, setRows] = useState<ParticipantRow[] | null>(null);
  const [pageProfiles, setPageProfiles] = useState<PageProfile[] | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasNext, setHasNext] = useState(false);

  const cacheRef = useRef<Map<string, Map<number, CachedPage>>>(new Map());
  const fieldModeRef = useRef<Map<string, SearchFieldMode>>(new Map());
  const appliedSearchRef = useRef({
    low: appliedSearch,
    raw: appliedSearchRaw,
  });
  appliedSearchRef.current = { low: appliedSearch, raw: appliedSearchRaw };

  const computeHasNext = useCallback((p: number, searchKey: string) => {
    const pageMap = cacheRef.current.get(searchKey);
    const next = pageMap?.has(p + 1) ?? false;
    const cur = pageMap?.get(p);
    setHasNext(next || !!cur?.hasMore);
  }, []);

  const loadPage = useCallback(
    async (targetPage: number) => {
      const { low: appliedLow, raw: appliedRaw } = appliedSearchRef.current;
      const searchKey =
        appliedLow.length > 0 ? `${appliedLow}\n${appliedRaw}` : "__all__";
      const pageMap = getOrCreatePageMap(cacheRef.current, searchKey);

      setError(null);
      setLoading(true);
      try {
        const cachedHit = pageMap.get(targetPage);
        if (cachedHit) {
          setRows(cachedHit.rows);
          setPageProfiles(cachedHit.pageProfiles);
          setPageIndex(targetPage);
          computeHasNext(targetPage, searchKey);
          return;
        }

        const excludedUserIdsFromPriorPages: string[] = [];
        for (let p = 0; p < targetPage; p++) {
          const prevP = pageMap.get(p);
          if (prevP?.includedUserIds?.length) {
            excludedUserIdsFromPriorPages.push(...prevP.includedUserIds);
          }
        }

        const searchOptions =
          appliedLow.length > 0
            ? targetPage === 0
              ? {
                  nameSearch: appliedLow,
                  nameSearchRaw: appliedRaw,
                  resolvedSearchField: null,
                  legacyNamePrefix: null,
                  excludedUserIdsFromPriorPages,
                }
              : (() => {
                  const mode = fieldModeRef.current.get(searchKey);
                  return {
                    nameSearch: appliedLow,
                    nameSearchRaw: appliedRaw,
                    resolvedSearchField: mode?.resolvedSearchField ?? null,
                    legacyNamePrefix: mode?.legacyNamePrefix ?? null,
                    excludedUserIdsFromPriorPages,
                  };
                })()
            : { excludedUserIdsFromPriorPages };

        if (targetPage === 0) {
          const res = await getParticipantTestResultsPage(
            PAGE_SIZE,
            null,
            searchOptions
          );
          if (appliedLow.length > 0) {
            const r = res.resolvedSearchField;
            fieldModeRef.current.set(searchKey, {
              resolvedSearchField:
                r === "name" || r === "name_lower" ? r : null,
              legacyNamePrefix: res.legacyNamePrefix ?? null,
            });
          }
          pageMap.set(0, {
            rows: res.rows as ParticipantRow[],
            pageProfiles:
              (res as { pageProfiles?: PageProfile[] }).pageProfiles ?? [],
            lastVisible: res.lastVisible,
            hasMore: res.hasMore,
            includedUserIds: res.includedUserIds ?? [],
          });
          setRows(res.rows as ParticipantRow[]);
          setPageProfiles(
            (res as { pageProfiles?: PageProfile[] }).pageProfiles ?? []
          );
          setPageIndex(0);
          computeHasNext(0, searchKey);
          return;
        }

        const prev = pageMap.get(targetPage - 1);
        if (!prev?.lastVisible) {
          setError("Missing previous page cursor.");
          return;
        }

        const res = await getParticipantTestResultsPage(
          PAGE_SIZE,
          prev.lastVisible,
          searchOptions
        );
        pageMap.set(targetPage, {
          rows: res.rows as ParticipantRow[],
          pageProfiles:
            (res as { pageProfiles?: PageProfile[] }).pageProfiles ?? [],
          lastVisible: res.lastVisible,
          hasMore: res.hasMore,
          includedUserIds: res.includedUserIds ?? [],
        });
        setRows(res.rows as ParticipantRow[]);
        setPageProfiles(
          (res as { pageProfiles?: PageProfile[] }).pageProfiles ?? []
        );
        setPageIndex(targetPage);
        computeHasNext(targetPage, searchKey);
      } catch (err: unknown) {
        const code =
          err && typeof err === "object" && "code" in err
            ? String((err as { code?: string }).code)
            : "";
        const message = err instanceof Error ? err.message : String(err);
        setError(
          code === "permission-denied"
            ? `Access denied. Sign in with an allowed account, and ensure Firestore rules allow those accounts to read the "users" collection (see firestore.rules in the repo).`
            : `Failed to load results: ${message}`
        );
        setRows([]);
        setPageProfiles(null);
      } finally {
        setLoading(false);
      }
    },
    [computeHasNext]
  );

  const loadPageRef = useRef(loadPage);
  loadPageRef.current = loadPage;

  // Only reset when the search filter changes — not when `loadPage` identity changes,
  // or Next/Previous would reload page 0 and clear the cache.
  useEffect(() => {
    cacheRef.current = new Map();
    fieldModeRef.current = new Map();
    void loadPageRef.current(0);
  }, [appliedSearch, appliedSearchRaw]);

  useEffect(() => {
    if (location.search) {
      navigate({ pathname: location.pathname, search: "" }, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  const handleApplySearch = () => {
    const raw = searchInput.trim();
    setAppliedSearch(raw.toLowerCase());
    setAppliedSearchRaw(raw);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setAppliedSearch("");
    setAppliedSearchRaw("");
  };

  const handlePrev = () => {
    if (pageIndex <= 0) return;
    void loadPage(pageIndex - 1);
  };

  const handleNext = () => {
    if (!hasNext) return;
    void loadPage(pageIndex + 1);
  };

  const empty = pageProfiles !== null && pageProfiles.length === 0 && !error;
  const searchActive = appliedSearch.length > 0;

  const groupedUsers = useMemo(
    () =>
      pageProfiles && pageProfiles.length
        ? groupsFromPage(rows ?? [], pageProfiles)
        : [],
    [rows, pageProfiles]
  );

  const [expandedByUser, setExpandedByUser] = useState<Record<string, boolean>>(
    {}
  );
  const isUserExpanded = (userId: string) => expandedByUser[userId] !== false;
  const toggleUser = (userId: string) => {
    setExpandedByUser(prev => {
      const open = prev[userId] !== false;
      return { ...prev, [userId]: !open };
    });
  };

  const hasTable = pageProfiles !== null && pageProfiles.length > 0;

  return (
    <div
      className={
        hasTable
          ? `${styles.wrapper} ${styles.wrapperWithTable}`
          : styles.wrapper
      }
    >
      <Link
        component={RouterLink}
        to={`/app/${pages.securityAwareness}`}
        className={styles.backLink}
        underline="hover"
      >
        ← Back to Security Awareness
      </Link>

      <Typography
        variant="h3"
        fontSize="40px"
        fontWeight={700}
        marginBottom={1}
      >
        Participant test results
      </Typography>
      <Box
        display="flex"
        flexWrap="wrap"
        gap={2}
        alignItems="flex-start"
        marginBottom={3}
      >
        <TextField
          size="small"
          label="Search by name"
          placeholder="First letters…"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleApplySearch();
            }
          }}
          sx={{ minWidth: 260 }}
        />
        <Button variant="contained" onClick={handleApplySearch}>
          Search
        </Button>
        <Button
          variant="outlined"
          onClick={handleClearSearch}
          disabled={!searchInput && !searchActive}
        >
          Clear
        </Button>
      </Box>

      {loading && rows === null && (
        <Box display="flex" justifyContent="center" padding={4}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Typography color="error" marginBottom={2}>
          {error}
        </Typography>
      )}

      {empty && !loading && (
        <Typography color="text.secondary">
          {searchActive
            ? "No matching results (check spelling or try more letters at the start of the name)."
            : "No users yet."}
        </Typography>
      )}

      {pageProfiles !== null && pageProfiles.length > 0 && (
        <div className={styles.tableScrollHost}>
          <Paper
            elevation={1}
            sx={{
              borderRadius: 2,
              maxWidth: "100%",
              overflow: "hidden",
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <TableContainer
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: "auto",
              }}
            >
              <Table
                stickyHeader
                sx={{
                  tableLayout: "fixed",
                  width: "100%",
                }}
                aria-label="Participant test results"
              >
                <colgroup>
                  <col style={{ width: 48 }} />
                  <col style={{ width: "26%" }} />
                  <col style={{ width: "34%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "30%" }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        borderBottom: 2,
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                      }}
                    />
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        borderBottom: 2,
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                      }}
                    >
                      Participant
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        borderBottom: 2,
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                      }}
                    >
                      Topic
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 700,
                        borderBottom: 2,
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                      }}
                    >
                      Score
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 700,
                        borderBottom: 2,
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                      }}
                    >
                      Completed
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupedUsers.map(g => {
                    const open = isUserExpanded(g.user_id);
                    const participantCell = (
                      <>
                        <Typography fontWeight={600} component="span">
                          {displayLabel(g)}
                        </Typography>
                        {g.email && displayLabel(g) !== g.email.trim() && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            display="block"
                          >
                            {g.email}
                          </Typography>
                        )}
                        {!g.name?.trim() && !g.email?.trim() && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            ID: {g.user_id}
                          </Typography>
                        )}
                      </>
                    );

                    if (!open) {
                      return (
                        <TableRow key={`${g.user_id}-collapsed`} hover>
                          <TableCell sx={{ verticalAlign: "top" }}>
                            <IconButton
                              aria-label="expand row"
                              size="small"
                              onClick={() => toggleUser(g.user_id)}
                            >
                              <KeyboardArrowDownIcon />
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "top" }}>
                            {participantCell}
                          </TableCell>
                          <TableCell colSpan={3}>
                            <Chip
                              size="small"
                              label={
                                g.tests.length === 0
                                  ? "No completed tests — expand"
                                  : `${g.tests.length} test(s) — expand to view`
                              }
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    }

                    if (g.tests.length === 0) {
                      return (
                        <TableRow key={`${g.user_id}-no-tests`} hover>
                          <TableCell sx={{ verticalAlign: "top" }}>
                            <IconButton
                              aria-label="collapse row"
                              size="small"
                              onClick={() => toggleUser(g.user_id)}
                            >
                              <KeyboardArrowUpIcon />
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "top" }}>
                            {participantCell}
                          </TableCell>
                          <TableCell
                            colSpan={3}
                            sx={{ color: "text.secondary" }}
                          >
                            No completed tests
                          </TableCell>
                        </TableRow>
                      );
                    }

                    return (
                      <React.Fragment key={g.user_id}>
                        {g.tests.map((t, idx) => (
                          <TableRow key={t.id} hover>
                            {idx === 0 && (
                              <TableCell
                                rowSpan={g.tests.length}
                                sx={{ verticalAlign: "top" }}
                              >
                                <IconButton
                                  aria-label="collapse row"
                                  size="small"
                                  onClick={() => toggleUser(g.user_id)}
                                >
                                  <KeyboardArrowUpIcon />
                                </IconButton>
                              </TableCell>
                            )}
                            {idx === 0 && (
                              <TableCell
                                rowSpan={g.tests.length}
                                component="th"
                                scope="row"
                                sx={{ verticalAlign: "top" }}
                              >
                                {participantCell}
                              </TableCell>
                            )}
                            <TableCell
                              sx={{
                                wordBreak: "break-word",
                                verticalAlign: "top",
                              }}
                            >
                              {testCodeToTopicTitle[t.test_code] ?? t.test_code}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ verticalAlign: "top" }}
                            >
                              {t.score_percent}%
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                verticalAlign: "top",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {t.completed_at
                                ? new Date(t.completed_at).toLocaleString()
                                : "—"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
              flexWrap="wrap"
              flexShrink={0}
              sx={{
                px: 2,
                py: 2,
                mb: "20px",
                borderTop: 1,
                borderColor: "divider",
                bgcolor: "action.hover",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Page {pageIndex + 1}
                {searchActive
                  ? ` · “${appliedSearchRaw || appliedSearch}”`
                  : ""}
              </Typography>
              <Box display="flex" gap={1} alignItems="center">
                <Button
                  variant="outlined"
                  disabled={loading || pageIndex === 0}
                  onClick={handlePrev}
                >
                  Previous
                </Button>
                <Button
                  variant="outlined"
                  disabled={loading || !hasNext}
                  onClick={handleNext}
                >
                  Next
                </Button>
                {loading && <CircularProgress size={24} />}
              </Box>
            </Box>
          </Paper>
        </div>
      )}
    </div>
  );
};

export default ParticipantsTestResults;
